import { useEffect, useRef } from "react";
import { GO_SALARY } from "@grundbesitz/shared";
import type { GameOptions } from "@grundbesitz/shared";
import { useStore } from "../../lib/store";
import { NumberField } from "./NumberField";
import { Segmented } from "./Segmented";
import {
  CASH_MAX,
  CASH_MIN,
  CASH_PRESETS,
  CASH_STEP,
  TIME_LIMIT_DEFAULT,
  TIME_LIMIT_MAX,
  TIME_LIMIT_MIN,
  TIME_LIMIT_STEP,
  formatAmount,
  summarizeOptions,
} from "./lobbyLogic";

type HouseRuleKey = keyof GameOptions["houseRules"];

const HOUSE_RULES: { key: HouseRuleKey; title: string; text: string }[] = [
  {
    key: "freeParkingPot",
    title: "Frei-Parken-Topf",
    text: "Steuern und Strafen sammeln sich in der Mitte. Wer auf Frei Parken landet, bekommt den Topf.",
  },
  {
    key: "doubleGoOnLanding",
    title: "Doppeltes LOS-Geld",
    text: `Wer genau auf LOS landet, bekommt ${formatAmount(GO_SALARY * 2)} statt ${formatAmount(GO_SALARY)}.`,
  },
  {
    key: "noAuction",
    title: "Keine Versteigerung",
    text: "Lehnt ein Spieler den Kauf ab, bleibt das Feld bei der Bank.",
  },
  {
    key: "buildAfterAllStreetsSold",
    title: "Bauen erst nach Ausverkauf",
    text: "Häuser und Hotels gibt es erst, wenn alle Straßen einen Besitzer haben.",
  },
  {
    key: "dealStreets",
    title: "Straßen verteilen",
    text: "Zu Beginn werden alle Straßen zufällig verteilt. Jeder bekommt den gleichen Wert, keiner eine ganze Farbgruppe. Kleine Unterschiede gleicht die Bank mit Bargeld aus. Bahnhöfe und Werke bleiben zu kaufen.",
  },
];

export function OptionsPanel({ options, editable }: { options: GameOptions; editable: boolean }) {
  return (
    <section aria-labelledby="options-title">
      <h2 id="options-title" className="font-display text-xl font-semibold text-ink">
        Optionen
      </h2>
      {editable ? <OptionsEditor options={options} /> : <OptionsReadOnly options={options} />}
    </section>
  );
}

function OptionsReadOnly({ options }: { options: GameOptions }) {
  return (
    <>
      <p className="mt-1 text-sm text-ink-2">Der Host stellt diese Optionen ein.</p>
      <dl className="mt-3 divide-y divide-line border-y border-line">
        {summarizeOptions(options).map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-4 py-2.5">
            <dt className="text-sm text-ink-2">{row.label}</dt>
            <dd className="text-right text-sm font-medium text-ink">{row.value}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}

function OptionsEditor({ options }: { options: GameOptions }) {
  const setOptions = useStore((s) => s.setOptions);
  // Successive quick changes build on each other even before the server has echoed the first one.
  const latest = useRef(options);
  useEffect(() => {
    latest.current = options;
  }, [options]);

  const update = (patch: Partial<GameOptions>) => {
    const next: GameOptions = { ...latest.current, ...patch };
    latest.current = next;
    void setOptions(next);
  };
  const updateRule = (key: HouseRuleKey, on: boolean) => {
    update({ houseRules: { ...latest.current.houseRules, [key]: on } });
  };

  const timeLimitOn = options.timeLimitMinutes > 0;

  return (
    <div className="mt-3 space-y-5">
      <Segmented
        legend="Miete"
        hint={
          options.rentMode === "AUTO"
            ? "Die Miete wird automatisch abgebucht."
            : "Der Besitzer muss die Miete fordern, bevor der nächste Spieler würfelt. Sonst verfällt sie."
        }
        value={options.rentMode}
        options={[
          { value: "AUTO", label: "Automatisch" },
          { value: "CLAIM", label: "Regeltreu einfordern" },
        ]}
        onChange={(rentMode) => update({ rentMode })}
      />

      <Segmented
        legend="Zug-Timer"
        value={options.turnTimerSeconds}
        options={[
          { value: 0, label: "Aus" },
          { value: 30, label: "30 s" },
          { value: 60, label: "60 s" },
          { value: 90, label: "90 s" },
        ]}
        onChange={(turnTimerSeconds) => update({ turnTimerSeconds })}
      />

      <div className="space-y-2.5">
        <NumberField
          label="Startkapital"
          prefix="M"
          value={options.startingCash}
          min={CASH_MIN}
          max={CASH_MAX}
          step={CASH_STEP}
          errorText={`Zwischen ${formatAmount(CASH_MIN)} und ${formatAmount(CASH_MAX)}.`}
          onCommit={(startingCash) => update({ startingCash })}
        />
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Startkapital wählen">
          {CASH_PRESETS.map((amount) => (
            <button
              key={amount}
              type="button"
              aria-pressed={options.startingCash === amount}
              onClick={() => update({ startingCash: amount })}
              className="min-h-11 rounded-lg border border-line px-3 text-sm font-medium text-ink hover:bg-surface-2 aria-pressed:border-accent aria-pressed:bg-accent-soft"
            >
              {amount.toLocaleString("de-DE")}
              {amount === 1500 && <span className="text-ink-2"> (Standard)</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2.5">
        <Segmented
          legend="Zeitlimit"
          hint={timeLimitOn ? "Danach gewinnt, wer das höchste Vermögen hat." : undefined}
          value={timeLimitOn ? "on" : "off"}
          options={[
            { value: "off", label: "Aus" },
            { value: "on", label: "Nach Minuten" },
          ]}
          onChange={(v) => update({ timeLimitMinutes: v === "on" ? TIME_LIMIT_DEFAULT : 0 })}
        />
        {timeLimitOn && (
          <NumberField
            label="Spieldauer in Minuten"
            suffix="min"
            value={options.timeLimitMinutes}
            min={TIME_LIMIT_MIN}
            max={TIME_LIMIT_MAX}
            step={TIME_LIMIT_STEP}
            errorText={`Zwischen ${TIME_LIMIT_MIN} und ${TIME_LIMIT_MAX} Minuten.`}
            onCommit={(timeLimitMinutes) => update({ timeLimitMinutes })}
          />
        )}
      </div>

      <fieldset>
        <legend className="mb-1 text-sm font-semibold text-ink">Hausregeln</legend>
        <p className="mb-1 text-sm text-ink-2">Abweichungen von den Standardregeln. Standardmäßig ist alles aus.</p>
        <div className="divide-y divide-line border-y border-line">
          {HOUSE_RULES.map((rule) => (
            <label key={rule.key} className="flex cursor-pointer items-start gap-3 py-3">
              <input
                type="checkbox"
                className="mt-0.5 size-6 shrink-0 accent-[var(--accent)]"
                checked={options.houseRules[rule.key]}
                onChange={(e) => updateRule(rule.key, e.target.checked)}
              />
              <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-2 font-medium text-ink">
                  {rule.title}
                  <span className="rounded bg-accent-soft px-1.5 py-0.5 text-xs font-semibold text-ink">Hausregel</span>
                </span>
                <span className="mt-0.5 block text-sm text-ink-2">{rule.text}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>
    </div>
  );
}
